import { getWebSocketEndpoint } from './websocket-utilities';
import { getSherlockInstance } from './env-utilities';
import { oktaAuth } from '@/config/okta-config';

class WebSocketManager {
  private connections: Map<string, WebSocket> = new Map();
  private messageHandlers: Map<string, Set<(data: unknown) => void>> = new Map();
  private errorHandlers: Map<string, Set<(error: string) => void>> = new Map();

  async connect(chatId: string, onMessage: (data: unknown) => void, onError?: (error: string) => void): Promise<WebSocket> {
    console.log('[WebSocketManager] connect() called for chatId:', chatId);
    
    let socket = this.connections.get(chatId);
    
    if (!socket || socket.readyState === WebSocket.CLOSED) {
      console.log('[WebSocketManager] Creating new WebSocket connection for chatId:', chatId);
      const endpoint = getWebSocketEndpoint();
      const token = (await oktaAuth?.getAccessToken()) || '';
      const instance = getSherlockInstance();
      const url = `${endpoint}?host=ignite-ops-portal&instance=${instance}&token=${token}`;
      console.log('[WebSocketManager] Connecting to:', url.replace(/token=[^&]+/, 'token=***'));
      socket = new WebSocket(url);
      
      const handlers = new Set([onMessage]);
      this.messageHandlers.set(chatId, handlers);
      
      if (onError) {
        const errHandlers = new Set([onError]);
        this.errorHandlers.set(chatId, errHandlers);
      }

      socket.onmessage = (event) => {
        const data = JSON.parse(event.data);
        const handlers = this.messageHandlers.get(chatId);
        handlers?.forEach((handler) => handler(data));
      };

      socket.onopen = () => {
        console.log('[WebSocketManager] WebSocket connected for chatId:', chatId);
      };

      socket.onclose = (event) => {
        console.log('[WebSocketManager] WebSocket closed for chatId:', chatId, 'code:', event.code);
        const errorMsg = `Connection closed (code: ${event.code})`;
        const errHandlers = this.errorHandlers.get(chatId);
        errHandlers?.forEach((handler) => handler(errorMsg));
        this.connections.delete(chatId);
        this.messageHandlers.delete(chatId);
        this.errorHandlers.delete(chatId);
      };

      socket.onerror = (error) => {
        console.log('[WebSocketManager] WebSocket error for chatId:', chatId, error);
        const errorMsg = 'WebSocket connection error';
        const errHandlers = this.errorHandlers.get(chatId);
        errHandlers?.forEach((handler) => handler(errorMsg));
        socket?.close();
      };
      
      this.connections.set(chatId, socket);
    } else {
      console.log('[WebSocketManager] Reusing existing WebSocket for chatId:', chatId);
      const handlers = this.messageHandlers.get(chatId);
      handlers?.add(onMessage);
      if (onError) {
        const errHandlers = this.errorHandlers.get(chatId) || new Set();
        errHandlers.add(onError);
        this.errorHandlers.set(chatId, errHandlers);
      }
    }

    return socket;
  }

  disconnect(chatId: string, onMessage: (data: unknown) => void, onError?: (error: string) => void): void {
    console.log('[WebSocketManager] disconnect() called for chatId:', chatId);
    const handlers = this.messageHandlers.get(chatId);
    handlers?.delete(onMessage);
    
    if (onError) {
      const errHandlers = this.errorHandlers.get(chatId);
      errHandlers?.delete(onError);
    }
    
    if (!handlers || handlers.size === 0) {
      console.log('[WebSocketManager] No more handlers, closing socket for chatId:', chatId);
      const socket = this.connections.get(chatId);
      socket?.close();
      this.connections.delete(chatId);
      this.messageHandlers.delete(chatId);
      this.errorHandlers.delete(chatId);
    }
  }

  send(chatId: string, message: Record<string, unknown>): void {
    const socket = this.connections.get(chatId);
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(message));
    }
  }
}

export const wsManager = new WebSocketManager();
